import React, { Component } from 'react';
import isNumber from 'lodash/isNumber';
import isObject from 'lodash/isObject';
import ModalModule from '../modal';
import UI from '../ui';
import Utils from '../utils';
import Data from '../data';

const { ModalContext } = ModalModule;
const { WrappedButton, WrappedCheckbox, WrappedInputTextWithLabel } = UI;
const { Communication, Session, Util } = Utils;
const { InternalCapability } = Data;

class AutoFundAccount extends Component {
	constructor() {
		super();

		this.state = {
			isAutoPurchaseEnabled: false,
			autoPurchaseAmount: 0,
			autoPurchaseThreshold: 0,
			errorMessage: '',
			showSuccessMessage: false,
		};
	}

	componentDidMount() {
		this.getAutoPurchaseSettings();
	}

	getAutoPurchaseSettings = async () => {
		const { showError } = this.context;

		const response = await Communication.getAutoPurchaseSettings();

		if (Communication.isSuccessful(response)) {
			const { isAutoPurchaseEnabled, autoPurchaseAmount, autoPurchaseThreshold } = isObject(
				response.data
			)
				? Util.toCamelCase(response.data)
				: {};

			this.setState({
				isAutoPurchaseEnabled,
				autoPurchaseAmount: isNumber(autoPurchaseAmount) ? autoPurchaseAmount : 0,
				autoPurchaseThreshold: isNumber(autoPurchaseThreshold) ? autoPurchaseThreshold : 0,
			});
		} else {
			showError({ response });
		}
	};

	setAutoPurchaseSettings = async () => {
		const { showError } = this.context;
		const { closeAutoFundAccountModal, completionCallback } = this.props;
		const { isAutoPurchaseEnabled, autoPurchaseAmount, autoPurchaseThreshold } = this.state;

		const response = await Communication.setAutoPurchaseSettings({
			is_auto_purchase_enabled: isAutoPurchaseEnabled,
			auto_purchase_amount: isAutoPurchaseEnabled ? autoPurchaseAmount : 0.0,
			auto_purchase_threshold: isAutoPurchaseEnabled ? autoPurchaseThreshold : 0.0,
			currency: 'usd',
		});

		if (Communication.isSuccessful(response)) {
			this.getAutoPurchaseSettings();

			// refresh account info to update auto-fund data in legacy repo
			if (completionCallback) {
				completionCallback({ type: 'AutoFundAccount' });
			}

			// the method is available if this component is embedded in AutoFundAccountModal
			if (closeAutoFundAccountModal) {
				closeAutoFundAccountModal();
			} else {
				this.setState({
					showSuccessMessage: true,
				});
			}
		} else {
			showError({ response });
		}
	};

	onClickCheckbox = () => {
		this.setState(({ isAutoPurchaseEnabled }) => ({
			isAutoPurchaseEnabled: !isAutoPurchaseEnabled,
		}));
	};

	onChangeInput = ({ target: { name, value } }) => {
		this.setState({
			[name]: Number(value),
		});
	};

	onClickSave = () => {
		const { maxBalanceAmountAllowed } = Session.getBalance();
		const maxPostageBalance = isNumber(maxBalanceAmountAllowed) ? maxBalanceAmountAllowed : 0;
		const { isAutoPurchaseEnabled, autoPurchaseAmount } = this.state;
		let errorMessage = '';

		if (!Session.hasCapability(InternalCapability.ChangePaymentMethod)) {
			errorMessage =
				'Your account is not authorized to purchase postage. Please contact your account administrator.';
		} else if (isAutoPurchaseEnabled && autoPurchaseAmount < 100) {
			errorMessage = 'The minimum automatic purchase amount is $100.';
		} else if (isAutoPurchaseEnabled && autoPurchaseAmount > maxPostageBalance) {
			errorMessage = `Your maximum postage balance is $${maxPostageBalance}. Postage purchases cannot exceed this amount.`;
		}

		this.setState(
			{
				errorMessage,
			},
			() => {
				// make a call to save auto-buy settings if no errors
				if (errorMessage === '') {
					this.setAutoPurchaseSettings();
				}
			}
		);
	};

	render() {
		const {
			isAutoPurchaseEnabled,
			errorMessage,
			autoPurchaseAmount,
			showSuccessMessage,
			autoPurchaseThreshold,
		} = this.state;

		return (
			<div className="common-payment auto-fund-account p-grid p-dir-col">
				{showSuccessMessage ? (
					<>
						<div className="p-col-12">
							Auto-Fund Settings have been successfully updated.
						</div>
						<div className="p-col-12">Please close this window to continue.</div>
					</>
				) : (
					<>
						<div className="p-col-12">
							<WrappedCheckbox
								inputId="settings-autobuy"
								htmlFor="settings-autobuy"
								name="isAutoPurchaseEnabled"
								checked={isAutoPurchaseEnabled}
								onChange={this.onClickCheckbox}
								dataqa="settings-autobuyenabled"
							>
								Auto-Fund Account
							</WrappedCheckbox>
						</div>
						<div className="p-col-12">
							<WrappedInputTextWithLabel
								className={`${isAutoPurchaseEnabled ? '' : 'disabled'}`}
								label="Automatically purchase $:"
								uiElementConfig={{
									className: 'autofund-input',
									value: autoPurchaseAmount,
									name: 'autoPurchaseAmount',
									onChange: this.onChangeInput,
									keyfilter: 'pint',
									disabled: !isAutoPurchaseEnabled,
									dataqa: 'settings-purchaseamount',
								}}
							/>
						</div>
						<div className="p-col-12">
							<WrappedInputTextWithLabel
								className={`${isAutoPurchaseEnabled ? '' : 'disabled'}`}
								label="When balance is below $:"
								uiElementConfig={{
									className: 'autofund-input',
									value: autoPurchaseThreshold,
									name: 'autoPurchaseThreshold',
									onChange: this.onChangeInput,
									keyfilter: 'pint',
									disabled: !isAutoPurchaseEnabled,
									dataqa: 'settings-triggeramount',
								}}
							/>
						</div>
						<div className="p-col-12 error-area">
							{errorMessage && <p className="error-icon">{errorMessage}</p>}
						</div>
						<div className="p-col-12">
							<WrappedButton
								label="Save"
								onClick={this.onClickSave}
								dataqa="settings-auto-fund-save"
							/>
						</div>
					</>
				)}
			</div>
		);
	}
}

AutoFundAccount.contextType = ModalContext;

export default AutoFundAccount;
